import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Building2, Trophy, BookOpen, Gift, Calendar, Shield, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Navigation = ({ onBookClick, onPricingClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Pages with dark/blue backgrounds that need white text
  const PHYSIOLAB_CATEGORIES = ['hrbet', 'koleno', 'rama', 'vrat', 'glezenj', 'sportne-poskodbe', 'drza'];
  const darkBgPrefixes = ['/darilni-boni', '/podjetja', '/klubi', '/zavarovalnice'];
  const isDarkBgPage = darkBgPrefixes.some(page => location.pathname.startsWith(page))
    || location.pathname === '/physiolab'
    || PHYSIOLAB_CATEGORIES.some(cat => location.pathname === `/physiolab/${cat}`);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/storitve-fizioterapija-ljubljana', label: 'Storitve', scrollId: '#storitve' },
    { href: '/fizioterapija-ljubljana-sasa-panic', label: 'O meni', scrollId: '#o-meni' },
    { href: '/cenik-fizioterapija-ljubljana', label: 'Cenik', scrollId: '#cenik', isModal: true },
    { href: '/kontakt-fizioterapija-ljubljana', label: 'Kontakt', scrollId: '#kontakt' },
  ];

  const specialPages = [
    { href: '/physiolab', label: 'PhysioLab+', icon: BookOpen, highlight: true },
    { href: '/darilni-boni', label: 'Boni', icon: Gift, highlight: false },
    { href: '/podjetja', label: 'Podjetja', icon: Building2 },
    { href: '/klubi', label: 'Klubi', icon: Trophy },
    { href: '/zavarovalnice', label: 'Zavarovalnice', icon: Shield },
  ];

  const handleBookClick = () => {
    if (onBookClick) {
      onBookClick();
    } else {
      // If no onBookClick provided (on subpages), navigate to home and open modal
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('openBookingModal'));
        }, 500);
      } else {
        window.dispatchEvent(new CustomEvent('openBookingModal'));
      }
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (link) => {
    const { href, scrollId, isModal } = link;
    
    // If on homepage, scroll to section + update URL
    if (location.pathname === '/') {
      if (isModal && scrollId === '#cenik') {
        if (onPricingClick) {
          onPricingClick();
        } else {
          window.dispatchEvent(new CustomEvent('openPricingModal'));
        }
        window.history.pushState({}, '', href);
      } else if (scrollId) {
        const element = document.querySelector(scrollId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
        window.history.pushState({}, '', href);
      } else {
        navigate(href);
      }
    } else {
      // On subpage, navigate to the SEO page
      navigate(href);
    }
    setIsMobileMenuOpen(false);
  };

  // Text color based on page and scroll state
  const textColor = isScrolled 
    ? 'text-[#1a365d]' 
    : (isDarkBgPage ? 'text-white' : 'text-[#1a365d]');
  
  const textColorHover = isScrolled 
    ? 'hover:text-[#f87171]' 
    : (isDarkBgPage ? 'hover:text-[#f87171]' : 'hover:text-[#f87171]');

  return (
    <>
      <nav
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
              <img loading="lazy" decoding="async"
                src="/images/logo-fizioterapija-sasa-panic-beli.avif"
                alt="Fizioterapija Saša Panič Ljubljana"
                className={`h-10 w-auto transition-all duration-300 ${
                  !isScrolled && isDarkBgPage ? 'brightness-0 invert' : ''
                }`}
              />
            </a>

            {/* Desktop Navigation - All in one row */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Main nav links */}
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link)}
                  className={`nav-link ${textColor} ${textColorHover} font-medium transition-colors px-2 py-1 text-sm whitespace-nowrap`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
              
              <div className={`h-5 w-px mx-1 ${isScrolled || !isDarkBgPage ? 'bg-slate-300' : 'bg-white/30'}`} />
              
              {/* Special pages */}
              {specialPages.map((page) => (
                <Link
                  key={page.href}
                  to={page.href}
                  className={`flex items-center gap-1 font-medium transition-colors px-2 py-1 text-sm rounded-lg whitespace-nowrap ${
                    isScrolled ? 'hover:bg-slate-100' : (isDarkBgPage ? 'hover:bg-white/10' : 'hover:bg-slate-100')
                  } ${
                    page.highlight 
                      ? 'text-[#f87171] hover:text-[#e86161]' 
                      : (isScrolled ? 'text-[#1a365d] hover:text-[#f87171]' : (isDarkBgPage ? 'text-white hover:text-[#f87171]' : 'text-[#1a365d] hover:text-[#f87171]'))
                  }`}
                  data-testid={`nav-${page.href.replace('/', '')}`}
                >
                  <page.icon className="w-3.5 h-3.5" />
                  <span className="hidden xl:inline">{page.label}</span>
                </Link>
              ))}
              
              <div className={`h-5 w-px mx-1 ${isScrolled || !isDarkBgPage ? 'bg-slate-300' : 'bg-white/30'}`} />
              
              {/* Phone */}
              <a
                href="tel:+38640464886"
                className={`flex items-center gap-1.5 ${textColor} font-medium ${textColorHover} transition-colors px-2 text-sm whitespace-nowrap`}
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden xl:inline">040 464 886</span>
              </a>
              
              {/* CTA Button */}
              <Button
                onClick={handleBookClick}
                className="bg-[#f87171] hover:bg-[#f87171]/90 text-white rounded-full px-4 py-1.5 font-semibold text-sm btn-lift ml-1 whitespace-nowrap"
                data-testid="nav-book-btn"
              >
                Naročite se
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${textColor}`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-white z-40 lg:hidden transition-transform duration-300 overflow-y-auto ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <div className="pt-20 pb-6 px-6 min-h-full flex flex-col">
          {/* Navigation Links */}
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link)}
                className="text-xl font-semibold text-[#1a365d] text-left hover:text-[#f87171] transition-colors"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </div>
          
          {/* Special pages in mobile */}
          <div className="pt-4 mt-4 border-t border-slate-200 space-y-3">
            <p className="text-sm text-slate-500 font-medium">Posebne ponudbe</p>
            {[
              { href: '/physiolab', label: 'PhysioLab+', icon: BookOpen, highlight: true },
              { href: '/darilni-boni', label: 'Darilni boni', icon: Gift, highlight: false },
              { href: '/podjetja', label: 'Za podjetja', icon: Building2 },
              { href: '/klubi', label: 'Za klube', icon: Trophy },
              { href: '/zavarovalnice', label: 'Zavarovalnice', icon: Shield },
            ].map((page) => (
              <Link
                key={page.href}
                to={page.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 text-lg font-semibold transition-colors ${
                  page.highlight 
                    ? 'text-[#f87171] hover:text-[#e86161]' 
                    : 'text-[#1a365d] hover:text-[#f87171]'
                }`}
              >
                <page.icon className={`w-5 h-5 ${page.highlight ? 'text-[#f87171]' : 'text-[#f87171]'}`} />
                {page.label}
              </Link>
            ))}
          </div>
          
          {/* Quick Actions & CTA - Bottom */}
          <div className="mt-auto pt-4 border-t border-slate-200 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <a
                href="tel:+38640464886"
                className="flex flex-col items-center gap-2 bg-[#1a365d] text-white rounded-2xl p-4 hover:bg-[#2d4a7c] transition-colors"
              >
                <Phone className="w-6 h-6" />
                <span className="font-semibold text-sm">Pokliči</span>
              </a>
              <a
                href="https://maps.app.goo.gl/LMh3RZepmt6NsNaL8"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 bg-emerald-500 text-white rounded-2xl p-4 hover:bg-emerald-600 transition-colors"
              >
                <MapPin className="w-6 h-6" />
                <span className="font-semibold text-sm">Kako do nas</span>
              </a>
            </div>
            
            <Button
              onClick={handleBookClick}
              className="w-full bg-[#f87171] hover:bg-[#f87171]/90 text-white rounded-2xl py-4 font-semibold text-lg flex items-center justify-center gap-2"
              data-testid="mobile-book-btn"
            >
              <Calendar className="w-5 h-5" />
              Rezerviraj termin
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
